import axios from "axios";
import isEqual from "lodash/isEqual";
import { getUser, saveUser } from "../actions/auth";
import { clearStoredAuthSession, emitAuthSessionChange, hasAuthSession } from "./authSession";
import { reloadToSignIn } from "./fullPageNavigation";
import notify from "./notify";
import { markDeploymentPending } from "./pendingDeployment";

const API_HOST = "";

export const CancelToken = axios.CancelToken;
export const isCancel = axios.isCancel;

const HttpClient = axios.create({
  baseURL: API_HOST,
  withCredentials: true,
});

export function resetToken(reason = "session-cleared") {
  clearStoredAuthSession();
  emitAuthSessionChange(reason);
}

function updatePermissions(response) {
  if (!response.headers.permissions) {
    return;
  }
  const permissions = JSON.parse(response.headers.permissions);
  const user = getUser();
  if (user && !isEqual(user.Role.permissions, permissions)) {
    user.Role.permissions = permissions;
    saveUser(user);
  }
}

function updateDeployVersion(response) {
  if (!response.headers.version) {
    return;
  }
  const version = localStorage.getItem("version");
  if (version === response.headers.version) {
    return;
  }

  // Record the new version before navigating so the document this reload loads
  // observes a match and does not reload again.
  localStorage.setItem("version", response.headers.version);
  // The first response establishes the version for this tab. A mismatch means
  // the tab may still be holding an old document and its asset references.
  if (!version) {
    return;
  }

  // A long-suspended mobile tab can resume with stale CSS/JS from the previous
  // deployment, so this document has to be replaced with the current one.
  //
  // It is marked rather than reloaded here. Reloading the instant the new
  // deployment is noticed interrupts the user mid-task and discards their work;
  // the routing layer performs the load at the next navigation instead, where
  // the view is being torn down anyway.
  //
  // The session is deliberately left intact. A deployment does not invalidate
  // server sessions -- it only bumps a version row that this header reports --
  // so ending the session here logged every signed-in user out of a still-valid
  // session. A session that has genuinely ended comes back as a 401 and is
  // handled by the branch above.
  markDeploymentPending();
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const entry = document.cookie.split("; ").find((value) => value.startsWith(`${name}=`));
  return entry ? decodeURIComponent(entry.slice(name.length + 1)) : null;
}

function currentFrontendPage() {
  if (typeof window === "undefined") return null;
  const pathname = window.location?.pathname;
  return typeof pathname === "string" && pathname.startsWith("/") ? pathname.slice(0, 512) : null;
}

function safeRequestSummary(error) {
  const rawUrl = typeof error?.config?.url === "string" ? error.config.url.split(/[?#]/, 1)[0] : undefined;
  const url = rawUrl
    ?.replace(/(\/(?:activate|change-password)\/)[^/]+/gi, "$1[REDACTED]")
    .replace(/\/[A-Za-z0-9._~-]{20,}(?=\/|$)/g, "/[REDACTED]");
  return {
    method: error?.config?.method?.toUpperCase(),
    route: url,
    status: error?.response?.status,
  };
}

function handleResponseError(error) {
  if (isCancel(error)) {
    return Promise.reject(error);
  }
  // Network/timeout errors arrive without a response object; there's nothing to
  // branch on, so just let the caller handle the rejection.
  if (!error.response) {
    return Promise.reject(error);
  }
  const status = error.response.status;
  // 401 = NOT AUTHENTICATED (missing/invalid/expired token). This is the only
  // case where we end the session and force a new sign-in.
  if (status === 401) {
    if (error.config?.__logoutRequest === true) {
      return Promise.reject(error);
    }
    console.warn("AdvisorSpace authentication request rejected.", safeRequestSummary(error));
    // A request made before this tab established in-memory state cannot have
    // invalidated a session. The boundary will reconcile the cookie separately.
    if (error.config?.__authSessionAtDispatch !== true) {
      error.handled = true;
      return Promise.reject(error);
    }
    if (window.location.pathname !== "/signin" && window.location.pathname !== "/signout") {
      localStorage.setItem("_pathname", `${window.location.pathname}${window.location.search}`);
    }
    resetToken("unauthorized");
    if (window.location.pathname !== "/signin") {
      // Re-enter through the document so a suspended tab cannot show the
      // sign-in screen with stale or missing application assets.
      reloadToSignIn();
    }
    error.handled = true;
    return Promise.reject(error);
  }
  // 403 = AUTHENTICATED but NOT AUTHORIZED. Keep the session alive and surface a
  // non-destructive message. This is essential during impersonation, where the
  // impersonated user will legitimately hit actions outside their role.
  if (status === 403) {
    console.warn("AdvisorSpace authorization request rejected.", safeRequestSummary(error));
    notify("You don't have permission to do that.");
    error.handled = true;
    return Promise.reject(error);
  }
  // Everything else (409 conflicts, 422 validation, 5xx, etc.) is a normal
  // request error: never log out. Let the caller handle/display it.
  return Promise.reject(error);
}

function handleResponseSuccess(response) {
  updatePermissions(response);
  updateDeployVersion(response);
  return response;
}

// Add a response interceptor
HttpClient.interceptors.response.use(handleResponseSuccess, handleResponseError);

// Errors the interceptor has already fully handled (401 logout, 403 message) are
// still rejected so callers can react, but fire-and-forget requests have no
// `.catch()` and would otherwise log "Uncaught (in promise)". Silence only those;
// genuine unhandled rejections stay visible for debugging.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    if (event.reason?.handled) {
      event.preventDefault();
    }
  });

  // The recorded version is shared by every tab on this origin, so once one tab
  // records a new deployment the others compare against the new value and never
  // notice it themselves. This event fires only in the tabs that did not write,
  // which is exactly the set that still needs marking. They are marked, not
  // reloaded, so a tab holding work the user has not finished keeps it until
  // they navigate.
  window.addEventListener("storage", (event) => {
    if (event.key !== "version") return;
    if (!event.oldValue || !event.newValue) return;
    if (event.oldValue === event.newValue) return;
    markDeploymentPending();
  });
}

HttpClient.interceptors.request.use(
  (config) => {
    config.__authSessionAtDispatch = hasAuthSession();
    const method = String(config.method || "get").toUpperCase();
    config.headers.delete("x-access-token");
    delete config.headers["x-access-token"];
    delete config.headers["X-Access-Token"];
    config.headers.set("x-advisorspace-auth-transport", "cookie-v1");
    const frontendPage = currentFrontendPage();
    if (frontendPage) config.headers.set("x-advisorspace-page", frontendPage);
    else config.headers.delete("x-advisorspace-page");
    if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
      const csrfToken = readCookie("__Host-advisorspace_csrf");
      if (csrfToken) config.headers.set("x-csrf-token", csrfToken);
      else config.headers.delete("x-csrf-token");
    }
    return config;
  },
  null,
  { synchronous: true },
);

export default HttpClient;
